import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { F, th as palette, Over, Btn } from "@/lib/bricks";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Controls, List, Row, Empty, TextLink, Loading, humanize } from "@/components/calm/Calm";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { deskFilterTabs, filterByDeskTab, initialDeskTabFromSearch, isClosedProperty } from "@/lib/propertyFilters";
import { useAuth } from "@/contexts/AuthContext";
import { bookShootHref } from "@/lib/navConfig";
import { BookMediaLink } from "@/components/listing/BookMedia";

const PROPERTY_TYPES = ['condo', 'house', 'townhouse', 'commercial'];
const EMPTY_FORM = { address: '', property_type: 'condo', agent_id: '', scheduled_date: '', notes: '' };

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => initialDeskTabFromSearch(location.search));
  const [newListingModal, setNewListingModal] = useState(false);
  const [newListingLoading, setNewListingLoading] = useState(false);
  const [newListingForm, setNewListingForm] = useState(EMPTY_FORM);
  const [agents, setAgents] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  useEffect(() => {
    api.get("/properties")
      .then(r => { setProperties(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get("/agents").then(r => setAgents(r.data || [])).catch(() => {});
  }, []);

  const submitNewListing = async () => {
    if (!newListingForm.address.trim()) return toast.error("Address is required");
    if (!newListingForm.agent_id) return toast.error("Select an agent");
    setNewListingLoading(true);
    try {
      await api.post("/jobs", {
        property_address: newListingForm.address.trim(),
        property_type: newListingForm.property_type,
        agent_id: newListingForm.agent_id,
        scheduled_date: newListingForm.scheduled_date || null,
        notes: newListingForm.notes || null,
      });
      toast.success("Booking created and sent for dispatch");
      setNewListingModal(false);
      setNewListingForm(EMPTY_FORM);
      api.get("/properties").then(r => setProperties(r.data || [])).catch(() => {});
    } catch {
      toast.error("Failed to create booking");
    } finally {
      setNewListingLoading(false);
    }
  };

  if (loading) return <Loading />;

  const deskTabs = deskFilterTabs(properties);
  const filtered = filterByDeskTab(properties, activeTab);
  const closedCount = deskTabs.find(x => x.id === 'Closed')?.count || 0;
  const openCount = deskTabs.find(x => x.id === 'Open')?.count ?? (properties.length - closedCount);
  const files = properties.reduce((n, x) => n + (Number(x.files_count) || 0), 0);

  const libraryStatus = (prop) => {
    const st = String(prop.status || '').toLowerCase();
    if (isClosedProperty(prop) && !['completed', 'delivered'].includes(st)) return 'Closed, still live';
    if (['completed', 'delivered'].includes(st)) return 'Delivered';
    if (['in_progress', 'editing', 'processing'].includes(st)) return 'In production';
    if (['scheduled', 'assigned', 'pending'].includes(st)) return 'Shoot booked';
    return humanize(prop.status) || 'Open';
  };

  const recent = properties.filter(x => x.thumbnail_url).slice(0, 4);

  return (
    <Page testid="properties-page">
      <PageHeader
        title="Media library"
        subtitle="Every listing's photos, video and files in one place"
        action={<BookMediaLink dark={dark} href={bookShootHref({ user })} />}
      />

      <Stats items={[
        { label: 'Open listings', value: openCount },
        { label: 'Closed', value: closedCount },
        { label: 'Files', value: files },
      ]} />

      {recent.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 8 }} data-testid="properties-recent">
          {recent.map(prop => (
            <button key={prop.property_id} type="button" onClick={() => navigate(`/properties/${prop.property_id}`)} aria-label={prop.address || 'Open listing'}
              style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'block' }}>
              <img src={prop.thumbnail_url} alt={prop.address || ''} style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 2, display: 'block' }} />
            </button>
          ))}
        </div>
      )}

      <Controls>
        <StatusTabs testid="properties-status-filter" value={activeTab} onChange={setActiveTab}
          options={deskTabs.map(t => ({ key: t.id, label: t.id }))} />
      </Controls>

      {filtered.length === 0 ? (
        <Empty action={<TextLink href={bookShootHref({ user })}>Book media</TextLink>}>
          {properties.length === 0 ? 'No listings yet. Book a shoot and the media lands here.' : 'No listings in this view.'}
        </Empty>
      ) : (
        <List testid="properties-list">
          {filtered.map(prop => {
            const n = Number(prop.files_count) || 0;
            return (
              <Row
                key={prop.property_id}
                testid="property-row"
                onClick={() => navigate(`/properties/${prop.property_id}`)}
                title={prop.address || 'Untitled listing'}
                meta={[prop.agent_name, `${n} ${n === 1 ? 'file' : 'files'}`].filter(Boolean)}
                next={prop.last_activity ? [`Last activity ${fmtDay(prop.last_activity)}`] : [n ? 'Files ready' : 'Media arrives after the shoot']}
                status={libraryStatus(prop)}
              />
            );
          })}
        </List>
      )}

      <div>
        <TextLink testid="new-listing" onClick={() => setNewListingModal(true)}>Add a listing for an agent</TextLink>
      </div>

      {/* New Listing Modal */}
      <Dialog open={newListingModal} onOpenChange={setNewListingModal}>
        <DialogContent style={{ maxWidth:440, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, letterSpacing:-0.3, color:p.ink }}>New listing</DialogTitle>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:18, paddingTop:20 }}>
            <div>
              <Over color={p.ink3}>Property address</Over>
              <AddressAutocomplete
                value={newListingForm.address}
                autoFocus
                onChange={v => setNewListingForm(f => ({ ...f, address: v }))}
                onSelect={s => setNewListingForm(f => ({ ...f, address: s.label || f.address }))}
                placeholder="123 King St W, Toronto, ON"
                p={p}
                style={{ marginTop:10, width:'100%', padding:'11px 14px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}
              />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <Over color={p.ink3}>Property type</Over>
                <select
                  value={newListingForm.property_type}
                  onChange={e => setNewListingForm({ ...newListingForm, property_type: e.target.value })}
                  style={{ marginTop:10, width:'100%', padding:'11px 14px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' }}
                >
                  {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <Over color={p.ink3}>Scheduled date</Over>
                <input
                  type="date" value={newListingForm.scheduled_date}
                  onChange={e => setNewListingForm({ ...newListingForm, scheduled_date: e.target.value })}
                  style={{ marginTop:10, width:'100%', padding:'11px 14px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' }}
                />
              </div>
            </div>
            <div>
              <Over color={p.ink3}>Agent</Over>
              <select
                value={newListingForm.agent_id}
                onChange={e => setNewListingForm({ ...newListingForm, agent_id: e.target.value })}
                style={{ marginTop:10, width:'100%', padding:'11px 14px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:newListingForm.agent_id ? p.ink : p.ink3, background:p.bg, outline:'none', boxSizing:'border-box' }}
              >
                <option value="">Select an agent</option>
                {agents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <Over color={p.ink3}>Notes (optional)</Over>
              <textarea
                value={newListingForm.notes} rows={3}
                onChange={e => setNewListingForm({ ...newListingForm, notes: e.target.value })}
                placeholder="Anything the photographer should know"
                style={{ marginTop:10, width:'100%', padding:'11px 14px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', resize:'vertical', boxSizing:'border-box' }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop:28, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setNewListingModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={submitNewListing} disabled={newListingLoading}>
              {newListingLoading ? 'Creating…' : 'Create booking'}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
